import { gql, useMutation, useQuery } from "@apollo/client";
import ResponsiveCDNImage from "../../../components/common/image/responsive-cdn-image";
import { GetManagerQuery } from "../../../__generated__/graphql";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../core/auth";
import { notify } from "../../../utils/toast";

const GET_MANAGER = gql`
  query GetManager {
    manager {
      id
      name
      stores {
        id
        img
        title
        address
        todaysRevenue
      }
    }
  }
`;
const SELLER_LOGIN = gql(`
  mutation masterLoginByManager ($storeId: Int!) {
    masterLoginByManager(storeId: $storeId) {
      token
      error
    }
  }
`);

const ManagerHome = () => {
  const navigate = useNavigate();
  const { loginAdmin, logoutManager, changeLastPage } = useAuth();
  const { data } = useQuery<GetManagerQuery>(GET_MANAGER);
  const [login] = useMutation(SELLER_LOGIN);

  // 매출 포맷팅 함수
  const formatRevenue = (revenue: number) => {
    return revenue.toLocaleString("ko-KR") + "원";
  };

  // 총 매출 계산
  const totalRevenue =
    data?.manager?.stores.reduce((sum, store) => {
      return sum + (store?.todaysRevenue || 0);
    }, 0) || 0;

  const handleSelectStore = async (store: any) => {
    try {
      const response = await login({
        variables: { storeId: store.id },
      });

      const { token, error } = response.data.masterLoginByManager;

      if (error) {
        alert(`${error}`);
      } else {
        localStorage.setItem("sellerToken", token);

        await loginAdmin();
        navigate("/boss/home");
      }
    } catch (err) {
      console.error("Error during login:", err);
      alert("로그인 중 오류가 발생했습니다.");
    }
  };

  const handleLogout = async () => {
    if (window.confirm("로그아웃 하시겠습니까?")) {
      try {
        // 매니저 토큰 제거
        localStorage.removeItem("managerToken");

        // 인증 상태 업데이트
        await logoutManager();

        changeLastPage();
        // 로그인 페이지로 이동
        navigate("/");
        window.location.reload();
        notify("로그아웃되었습니다.");
      } catch (error) {
        console.error("Logout error:", error);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 헤더 */}
      <div className="bg-white shadow-sm border-b break-keep whitespace-nowrap">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex flex-col justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">매장 관리</h1>
              <div className="text-lg text-gray-600 mt-1 flex gap-1">
                <span className="block">안녕하세요, </span>
                <span className="block font-semibold text-blue-600">
                  {data?.manager?.name}님
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-sm text-blue-600 font-medium text-right">
                  오늘 총 매출
                </div>
                <div className="text-xl font-bold text-blue-700">
                  {formatRevenue(totalRevenue)}
                </div>
              </div>

              <button
                onClick={handleLogout}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg transition-colors duration-200 font-medium"
              >
                로그아웃
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 매장 목록 */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {data?.manager?.stores.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-gray-400 text-6xl mb-4">🏪</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              등록된 매장이 없습니다
            </h3>
            <p className="text-gray-500">첫 번째 매장을 등록해보세요!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data?.manager?.stores.map((store) => (
              <div
                key={store?.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200 cursor-pointer group"
              >
                {/* 매장 이미지 */}
                <div className="relative h-48 overflow-hidden">
                  {store.img ? (
                    <ResponsiveCDNImage
                      src={store.img}
                      alt={store.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                      <div className="text-gray-400 text-4xl">🏪</div>
                    </div>
                  )}

                  {/* 호버 오버레이 */}
                  <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-10 transition-opacity duration-200"></div>
                </div>

                {/* 매장 정보 */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-1">
                    {store?.title}
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-2 mb-3">
                    📍 {store?.address}
                  </p>

                  {/* 매출 섹션 */}
                  <div className="mb-4">
                    <div className="text-sm text-blue-600 font-medium mb-1">
                      오늘 매출
                    </div>
                    <div className="text-lg font-bold text-blue-700">
                      {formatRevenue(store?.todaysRevenue || 0)}
                    </div>
                  </div>

                  {/* 액션 버튼 */}
                  <div className="pt-2">
                    <button
                      onClick={() => handleSelectStore(store)}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
                    >
                      매장 접속하기
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManagerHome;
